import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Button, Col, Container, Label, Row } from "reactstrap";
import {
  FACEBOOK_POST_LOADING,
  INSTAGRAM_POST_LOADING,
  LINKED_IN_BUSINESS_POST_LOADING,
  LINKED_IN_POST_LOADING,
  OPENAI_GET_TEMPLATES_LOADING,
  TIKTOK_POST_LOADING,
  TWITTER_POST_LOADING,
} from "../../constants/loading.constant";
import {
  EDIT_IMAGE_MODAL,
  EDIT_VIDEO_MODAL,
  SCHEDULE_POST_MODAL,
  ADD_SOCIAL_IMAGE_MODAL,
  ADD_SOCIAL_VIDEO_MODAL,
  EMOJI_FORM,
  TEMPLATE_SOCIAL_FORM,
  TEMPLATES_SOCIAL_FORM,
  HASHTAGS_SOCIAL_FORM,
  ADD_MENTION_MODAL,
  OPENAI_SOCIAL_FORM,
} from "../../constants/modal.constant";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useIntl } from "react-intl";
import { useRouterCore } from "../../cores/router";
import { useSocialPostCore } from "../../cores/socialPost";
import { PickerMode, SocialProviderId } from "../../cores/socialPost/config";
import { SOCIAL_ACTION_TYPES } from "../../cores/socialPost/consts/socialActionType.constant";
import { SOCIAL_POST_TYPES } from "../../cores/socialPost/consts/socialPostType.constant";
import { PostType } from "../../cores/socialPost/enums/postType";
import { PanelWembii } from "../component/PanelWembii";
import { ModernTextEditor } from "../component/socialPost/creator/ModernTextEditor";
import { PostForm } from "../component/socialPost/PostForm";
import { SocialPostPreview } from "../component/socialPost/preview/SocialPostPreview";
import { ModernSocialPostSelector } from "../component/socialPost/selector/ModernSocialPostSelector";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import QueryString from "query-string";
import { useLocation } from "react-router-dom";
import { takePendingSocialPostImages } from "../../cores/helpers/pending-social-post-image";
import { ModernPostButtons } from "../component/socialPost/creator/ModernPostButtons";
import { PostActionButtons } from "../component/socialPost/creator/PostActionButtons";
import "../component/socialPost/creator/ModernPostContainer.scss";
import VideoForm from "../component/socialPost/modals/VideoForm";
import ImageForm from "../component/socialPost/modals/ImageForm";
import { SocialType } from "../../cores/socialPost/interfaces/postError";
import { EmojiForm } from "../component/EmojiForm";
import TemplateSocialForm from "../component/socialPost/modals/TemplateSocialForm";
import TemplatesForm from "../component/socialPost/modals/TemplatesForm";
import { useTemplateCore } from "../../cores/templates";
import { useInstagramCore } from "../../cores/instagram";
import { Errors } from "../../constants/errors";
import { ErrorIcon } from "../component/socialPost/ErrorIcon";
import HashtagsForm from "../component/socialPost/modals/HashtagsForm";
import { MentionForm } from "../component/socialPost/modals/MentionForm";
import { OpenAiIcon } from "../component/social/OpenAi/OpenAiIcon";
import OpenAiForm from "../component/socialPost/modals/OpenAiForm";
import { toast } from "react-toastify";
import { ProductGuideModal } from "../component/guides/ProductGuideModal";
import { SOCIAL_POST_GUIDE_STEPS } from "../component/guides/productGuideConfigs";
import { useProductGuideQuery } from "../../hooks/useProductGuideQuery";
import { useNotificationCore } from "../../cores/notifications";

export default function SocialPost() {
  const intl = useIntl();
  const { search } = useLocation();
  const { showGuide: showSocialGuide, closeGuide: closeSocialGuide } =
    useProductGuideQuery("social");
  const { getSocialTemplates } = useTemplateCore();
  const {
    getMeSocialSettings,
    socialSettings,
    company,
    companyRole,
    isAdmin,
    isReseller,
  } = useMeCore();
  const {
    socialProviders,
    selectedPost,
    action,
    postType,
    video,
    reset,
    addEmoji,
    updateMentions,
    inputCursorIndex,
    getHashtags,
    addImages,
    getSocialPosts,
  } = useSocialPostCore();
  const { errorCode } = useInstagramCore();
  const { goToSocial, goToSocialHistory, goToSocialCalendar } = useRouterCore();
  const {
    isModal,
    isLoading,
    getModalContent,
    getModalVisibility,
    turnOffModal,
    turnOnModal,
  } = useGlobalCore();
  const { getNotifications, getUnreadNotifications } =
    useNotificationCore();
  const [picker, setPicker] = useState<PickerMode>(PickerMode.Off);
  const [from, setFrom] = useState("");
  const [repost, setRepost] = useState(false);

  // Limpiar el posteador al entrar salvo cuando venimos a editar desde calendario / historial
  // (selectPost ya se disparó antes del navigate; reset() aquí lo borraba).
  useEffect(() => {
    const { from: fromParam } = QueryString.parse(search);
    const editFromElsewhere =
      fromParam === "calendar" || fromParam === "history";
    if (!editFromElsewhere) {
      reset();
    }
  }, [reset, search]);

  /** Imagen preparada desde IA (texto a imagen / generación) guardada en sessionStorage */
  useEffect(() => {
    const { from: fromParam } = QueryString.parse(search);
    if (fromParam !== "ai-image") return;
    const pending = takePendingSocialPostImages();
    if (pending?.length) {
      addImages(pending);
      toast.info(
        intl.formatMessage({
          id: "app.aiGeneration.postToSocialSelectNetworkHint",
        }),
        { autoClose: 8000 }
      );
    }
  }, [search, addImages, intl]);

  const error = useMemo(() => {
    switch (errorCode) {
      case Errors.NotBusinessAccount:
        return (
          <Label className="text-muted text-center my-4 w-100">
            {intl.formatMessage({ id: "app.social.instagram.businessError" })}.
            {" Lee "}
            <a
              href="https://soporte.wembii.com/es/article/como-cambiar-una-cuenta-de-instagram-a-empresa-1d9gri7/"
              target="_blank"
              rel="noopener noreferrer"
            >
              aquí
            </a>
            {" como hacerlo"}
          </Label>
        );
    }
  }, [errorCode, intl]);

  useEffect(() => {
    if (
      !companyRole?.social &&
      !companyRole?.owner &&
      !companyRole?.admin &&
      !companyRole?.agent &&
      !isAdmin &&
      !isReseller
    )
      goToSocial();
  }, [goToSocial, companyRole, isAdmin, isReseller]);

  // La limpieza del estado se maneja en el resetPost function

  const loadingIds = useMemo(
    (): string[] => [
      FACEBOOK_POST_LOADING,
      TWITTER_POST_LOADING,
      LINKED_IN_BUSINESS_POST_LOADING,
      LINKED_IN_POST_LOADING,
      INSTAGRAM_POST_LOADING,
      TIKTOK_POST_LOADING,
    ],
    []
  );

  /** Reel IG + programar: texto “Programando” sin spinner de carga pesada. */
  const instagramReelScheduleProgramming = useMemo(() => {
    if (action !== SOCIAL_ACTION_TYPES.SCHEDULE) return false;
    if (!isLoading(INSTAGRAM_POST_LOADING)) return false;
    const hasInstagram = socialProviders.some(
      (sp: any) =>
        sp?.id === SocialProviderId.Instagram ||
        sp?.name === SocialProviderId.Instagram
    );
    if (!hasInstagram) return false;
    const isReel =
      postType === PostType.REEL ||
      selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO;
    if (!isReel) return false;
    return Boolean(
      video ||
        (selectedPost?.post?.type === SOCIAL_POST_TYPES.VIDEO &&
          (selectedPost?.media?.length ?? 0) > 0)
    );
  }, [
    action,
    isLoading,
    socialProviders,
    postType,
    selectedPost?.post?.type,
    selectedPost?.media?.length,
    video,
  ]);

  // Publicar/programar puede tardar por subida de archivos y APIs externas;
  // mantenemos el composer usable mientras el proceso sigue en background.
  const shouldDisplay = true;

  useEffect(() => {
    const { from, repost } = QueryString.parse(search);
    if (from && from !== "") {
      setFrom(from as string);
    }
    if (repost && repost !== "") {
      setRepost((repost as string) === "true");
    }
  }, [search]);

  useEffect(() => {
    getMeSocialSettings(company!.id);
  }, [company, getMeSocialSettings]);

  const openMentionsModal = useCallback(() => {
    turnOnModal(ADD_MENTION_MODAL, true);
  }, [turnOnModal]);

  const resetPost = useCallback(() => {
    setPicker(PickerMode.Off);
    reset();
  }, [reset]);

  const refreshAfterScheduleSuccess = useCallback(() => {
    if (!company?.id) return;
    const since = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
    const until = moment().add(18, "months").endOf("month").format("YYYY-MM-DD");
    const run = () => {
      getSocialPosts(company.id, since, until, "all", "", "post");
      void getNotifications(company.id, 1, { type: "short" });
      void getUnreadNotifications(company.id);
    };
    run();
    window.setTimeout(run, 2200);
  }, [
    company?.id,
    getNotifications,
    getUnreadNotifications,
    getSocialPosts,
  ]);

  const handleAddEmoji = useCallback(
    (emoji: any) => {
      const unifieds = emoji.unified.split("-");
      const emojiCode = unifieds
        .map((unified: any) => String.fromCodePoint(Number(`0x${unified}`)))
        .join("");
      addEmoji(emojiCode);
      updateMentions(emojiCode.length, inputCursorIndex as number);
    },
    [addEmoji, updateMentions, inputCursorIndex]
  );

  const openEmojiModal = useCallback(() => {
    turnOnModal(EMOJI_FORM, <EmojiForm onAdding={handleAddEmoji} />);
  }, [turnOnModal, handleAddEmoji]);

  const openImagePickerModal = useCallback(() => {
    turnOnModal(ADD_SOCIAL_IMAGE_MODAL, true);
  }, [turnOnModal]);

  const openHashtagModal = useCallback(() => {
    getHashtags(company?.id!);
    turnOnModal(HASHTAGS_SOCIAL_FORM, true);
  }, [turnOnModal, getHashtags, company]);

  const openTemplateModal = useCallback(() => {
    getSocialTemplates(company?.id!);
    turnOnModal(TEMPLATES_SOCIAL_FORM, true);
  }, [turnOnModal, getSocialTemplates, company]);

  const openOpenAiModal = useCallback(() => {
    turnOnModal(OPENAI_SOCIAL_FORM, true);
  }, [turnOnModal]);

  const openVideoPickerModal = useCallback(() => {
    turnOnModal(ADD_SOCIAL_VIDEO_MODAL, true);
  }, [turnOnModal]);

  const cancelEdition = useCallback(() => {
    resetPost();
    if (from === "history") {
      goToSocialHistory();
    } else if (from === "calendar") {
      goToSocialCalendar();
    } else {
      // Default behavior when from is empty or unknown
      goToSocialCalendar();
    }
  }, [goToSocialHistory, goToSocialCalendar, resetPost, from]);

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.socialMediaPost.title" })}
        subtitle={
          selectedPost
            ? intl.formatMessage({ id: "app.socialMediaPost.modifyPost" })
            : undefined
        }
        mb={false}
        rightChildren={
          <>
            {(socialProviders as any[]).length > 0 && (socialSettings as any)?.openai && (
              <Button className="whiteButton" onClick={openOpenAiModal}>
                <OpenAiIcon size="1x" active={true} className="mr-2" />
                {intl.formatMessage({ id: "app.socialPost.openai" })}
              </Button>
            )}
            {selectedPost && (
              <div className="d-flex">
                {!repost && (
                  <PostActionButtons from={from} onReset={resetPost} />
                )}
                <Button color="danger" onClick={cancelEdition}>
                  {intl.formatMessage({ id: "app.socialMediaPost.cancel" })}
                </Button>
              </div>
            )}
          </>
        }
      />
      <Content>
        <UpdateLoadingCard
          loadingId={loadingIds}
          text={
            instagramReelScheduleProgramming
              ? "Programando"
              : picker === PickerMode.Video
              ? intl.formatMessage({ id: "app.socialMediaPost.preparingVideo" })
              : action === SOCIAL_ACTION_TYPES.POST
              ? intl.formatMessage({ id: "app.socialMediaPost.posting" })
              : intl.formatMessage({ id: "app.socialMediaPost.programming" })
          }
        />

        {shouldDisplay && (
          <Container fluid className="modern-multiposter">
            {errorCode && (
              <Row className="d-flex justify-content-center">
                <Col xs="12" md="8">
                  <PanelWembii className="mb-2">
                    <div className="d-flex justify-content-center align-items-center">
                      <ErrorIcon size="2x" className="m-2" />
                      {error}
                    </div>
                  </PanelWembii>
                </Col>
              </Row>
            )}

            {/* Main Content Area */}
            <Row className="d-flex justify-content-center">
              <Col xs="12" lg="6">
                <div className="pt-4">
                  <PanelWembii shadow className="p-0">
                    <div className="modern-post-container">
                      {/* Social Media Selector inside the main container */}
                      <div className="social-selector-section">
                        <ModernSocialPostSelector socialType={SocialType.Post} />
                      </div>

                      {/* Post Type Selector ahora integrado en Instagram */}

                      <ModernTextEditor
                        activePicker={picker}
                        onClickEmoji={openEmojiModal}
                        onClickHashtag={openHashtagModal}
                        onClickMention={openMentionsModal}
                        onClickImages={openImagePickerModal}
                        onClickVideo={openVideoPickerModal}
                        onClickTemplate={openTemplateModal}
                        onClickOpenAi={openOpenAiModal}
                        socialType={SocialType.Post}
                      />

                      <ModernPostButtons
                        repost={repost}
                        from={from}
                        onReset={resetPost}
                      />
                    </div>
                  </PanelWembii>
                </div>
              </Col>
              <Col xs="12" lg="6">
                <div className="pt-4 modern-preview-container">
                  <PanelWembii shadow className="p-3 px-3 px-md-4">
                    <h4 className="modern-post-section-title mb-3">
                      {intl.formatMessage({
                        id: "app.socialMediaPost.preview",
                      })}
                    </h4>
                    <SocialPostPreview />
                  </PanelWembii>
                </div>
              </Col>
            </Row>
          </Container>
        )}
      </Content>
      <SideModal
        active={isModal(SCHEDULE_POST_MODAL)}
        visible={getModalVisibility(SCHEDULE_POST_MODAL)}
        onClose={() => turnOffModal(SCHEDULE_POST_MODAL)}
      >
        <PostForm
          onClose={() => turnOffModal(SCHEDULE_POST_MODAL)}
          onReset={resetPost}
          repost={repost}
          socialType={SocialType.Post}
          onAfterScheduleSuccess={refreshAfterScheduleSuccess}
        />
      </SideModal>
      <SideModal
        active={isModal(ADD_SOCIAL_IMAGE_MODAL)}
        visible={getModalVisibility(ADD_SOCIAL_IMAGE_MODAL)}
        onClose={() => turnOffModal(ADD_SOCIAL_IMAGE_MODAL)}
      >
        <ImageForm />
      </SideModal>
      <SideModal
        active={isModal(ADD_SOCIAL_VIDEO_MODAL)}
        visible={getModalVisibility(ADD_SOCIAL_VIDEO_MODAL)}
        onClose={() => turnOffModal(ADD_SOCIAL_VIDEO_MODAL)}
      >
        <VideoForm />
      </SideModal>
      <SideModal
        active={isModal(EMOJI_FORM)}
        visible={getModalVisibility(EMOJI_FORM)}
        onClose={() => turnOffModal(EMOJI_FORM)}
      >
        {getModalContent(EMOJI_FORM)}
      </SideModal>
      <SideModal
        active={isModal(TEMPLATES_SOCIAL_FORM)}
        visible={getModalVisibility(TEMPLATES_SOCIAL_FORM)}
        onClose={() => turnOffModal(TEMPLATES_SOCIAL_FORM)}
      >
        <TemplatesForm />
      </SideModal>
      <SideModal
        active={isModal(OPENAI_SOCIAL_FORM)}
        visible={getModalVisibility(OPENAI_SOCIAL_FORM)}
        onClose={() => turnOffModal(OPENAI_SOCIAL_FORM)}
        showSppiner={isLoading(OPENAI_GET_TEMPLATES_LOADING)}
      >
        <OpenAiForm />
      </SideModal>
      <SideModal
        active={isModal(ADD_MENTION_MODAL)}
        visible={getModalVisibility(ADD_MENTION_MODAL)}
        onClose={() => turnOffModal(ADD_MENTION_MODAL)}
      >
        <MentionForm />
      </SideModal>
      <SideModal
        active={isModal(HASHTAGS_SOCIAL_FORM)}
        visible={getModalVisibility(HASHTAGS_SOCIAL_FORM)}
        onClose={() => turnOffModal(HASHTAGS_SOCIAL_FORM)}
      >
        <HashtagsForm />
      </SideModal>
      <SideModal
        active={isModal(EDIT_IMAGE_MODAL)}
        visible={getModalVisibility(EDIT_IMAGE_MODAL)}
        onClose={() => turnOffModal(EDIT_IMAGE_MODAL)}
        left
      >
        {getModalContent(EDIT_IMAGE_MODAL)}
      </SideModal>
      <SideModal
        active={isModal(EDIT_VIDEO_MODAL)}
        visible={getModalVisibility(EDIT_VIDEO_MODAL)}
        onClose={() => turnOffModal(EDIT_VIDEO_MODAL)}
        left
      >
        {getModalContent(EDIT_VIDEO_MODAL)}
      </SideModal>
      <SideModal
        active={isModal(TEMPLATE_SOCIAL_FORM)}
        visible={getModalVisibility(TEMPLATE_SOCIAL_FORM)}
        onClose={() => turnOffModal(TEMPLATE_SOCIAL_FORM)}
        left
      >
        <TemplateSocialForm />
      </SideModal>
      <ProductGuideModal
        isOpen={showSocialGuide}
        onClose={closeSocialGuide}
        modalTitleId="app.socialPost.guide.modalTitle"
        steps={SOCIAL_POST_GUIDE_STEPS}
        persistDismissKey="social"
      />
    </Layout>
  );
}
